/**
 * Devanagari to Latin, for reading only.
 *
 * Hindi calls come back in Devanagari because `SARVAM_MODE` defaults to `transcribe`, and a
 * wall of twenty-five tiles of Devanagari cannot be scanned at a glance by a supervisor who
 * does not read the script fluently -- which is most of the operational value of the wall.
 *
 * This is deliberately *display-side*. The obvious alternative is to switch the STT to its
 * `translit` mode and get Latin transcripts for free, but that changes what the LLM sees on
 * every single turn of every call. Transliterating in the browser changes nothing about the
 * conversation.
 *
 * The target is readability, not scholarship: `mera phone number hai`, the way someone would
 * actually type it, rather than a strict IAST `merā phona nambara`. Inherent-vowel deletion
 * is approximated (see `stripFinalSchwa`), which is what makes the difference between
 * "namaste" and "namasate".
 */

const VOWELS: Record<string, string> = {
  "अ": "a", "आ": "aa", "इ": "i", "ई": "ee", "उ": "u", "ऊ": "oo",
  "ऋ": "ri", "ए": "e", "ऐ": "ai", "ओ": "o", "औ": "au",
  "ऑ": "o", "ऍ": "e",
};

/** Vowel signs (matras), which replace a consonant's inherent "a". */
const MATRAS: Record<string, string> = {
  "ा": "aa", "ि": "i", "ी": "ee", "ु": "u", "ू": "oo",
  "ृ": "ri", "े": "e", "ै": "ai", "ो": "o", "ौ": "au",
  // Candra forms, which exist to write English loanwords -- "appointment", "doctor",
  // "call". Omitting them leaves raw Devanagari sitting in the middle of a Latin word,
  // and loanwords are exactly the vocabulary a supervisor scans for.
  "ॉ": "o", "ॅ": "e",
};

const CONSONANTS: Record<string, string> = {
  "क": "k", "ख": "kh", "ग": "g", "घ": "gh", "ङ": "ng",
  "च": "ch", "छ": "chh", "ज": "j", "झ": "jh", "ञ": "n",
  "ट": "t", "ठ": "th", "ड": "d", "ढ": "dh", "ण": "n",
  "त": "t", "थ": "th", "द": "d", "ध": "dh", "न": "n",
  "प": "p", "फ": "ph", "ब": "b", "भ": "bh", "म": "m",
  "य": "y", "र": "r", "ल": "l", "व": "v",
  "श": "sh", "ष": "sh", "स": "s", "ह": "h",
  "ळ": "l",
  // Precomposed nukta forms. Loanword sounds, and common in exactly the vocabulary a
  // supervisor is scanning for: "zaroori", "form", "qeemat".
  "क़": "q", "ख़": "kh", "ग़": "gh", "ज़": "z", "ड़": "r", "ढ़": "rh", "फ़": "f", "य़": "y",
};

const DIGITS: Record<string, string> = {
  "०": "0", "१": "1", "२": "2", "३": "3", "४": "4",
  "५": "5", "६": "6", "७": "7", "८": "8", "९": "9",
};

const VIRAMA = "्";
const NUKTA = "़";
const ANUSVARA = "ं";
const CHANDRABINDU = "ँ";
const VISARGA = "ः";

/** Nukta applied as a combining mark rather than a precomposed character. */
const NUKTA_FORMS: Record<string, string> = {
  "क": "q", "ख": "kh", "ग": "gh", "ज": "z", "ड": "r", "ढ": "rh", "फ": "f",
};

const DEVANAGARI = /[ऀ-ॿ]/;


/** Whether a string contains any Devanagari at all. */
export function hasDevanagari(text: string): boolean {
  return DEVANAGARI.test(text);
}

/**
 * Normalise a word ending so it reads the way Hinglish is actually typed.
 *
 * Two separate corrections, both at the end of a word:
 *
 * 1. Written Devanagari keeps the inherent vowel that spoken Hindi drops, so a naive
 *    mapping yields "namasate" for नमस्ते and "kara" for कर.
 * 2. A final long "aa" is phonetically correct but nobody writes it: मेरा and क्या are
 *    typed "mera" and "kya", not "meraa" and "kyaa".
 *
 * Neither rule is exact -- Hindi schwa deletion genuinely needs a lexicon -- but a
 * supervisor skimming a tile needs recognisable, not correct.
 */
function normalizeWordEnding(word: string): string {
  if (word.endsWith("aa")) return word.slice(0, -1);
  if (word.length > 2 && word.endsWith("a")) return word.slice(0, -1);
  return word;
}

function transliterateWord(word: string): string {
  let out = "";
  let index = 0;

  while (index < word.length) {
    const char = word[index];
    const next = word[index + 1];

    if (CONSONANTS[char] !== undefined) {
      // A combining nukta changes the consonant outright, so resolve it before anything else.
      let sound = CONSONANTS[char];
      let cursor = index + 1;
      if (next === NUKTA) {
        sound = NUKTA_FORMS[char] ?? sound;
        cursor += 1;
      }

      const following = word[cursor];
      if (following === VIRAMA) {
        // Virama kills the inherent vowel: this consonant joins the next one.
        out += sound;
        index = cursor + 1;
        continue;
      }
      if (following !== undefined && MATRAS[following] !== undefined) {
        out += sound + MATRAS[following];
        index = cursor + 1;
        continue;
      }
      // No sign at all means the inherent "a" is pronounced.
      out += sound + "a";
      index = cursor;
      continue;
    }

    if (VOWELS[char] !== undefined) {
      out += VOWELS[char];
    } else if (DIGITS[char] !== undefined) {
      out += DIGITS[char];
    } else if (char === ANUSVARA || char === CHANDRABINDU) {
      out += "n";
    } else if (char === VISARGA) {
      out += "h";
    } else if (MATRAS[char] !== undefined) {
      // A stray matra with no consonant before it: keep the sound rather than dropping it.
      out += MATRAS[char];
    } else if (char !== VIRAMA && char !== NUKTA) {
      out += char;
    }
    index += 1;
  }

  return normalizeWordEnding(out);
}

/**
 * Transliterate the Devanagari runs in `text`, leaving everything else untouched.
 *
 * Latin passes straight through, so code-mixed Hinglish ("मेरा phone number है") keeps the
 * English words exactly as written -- including brand names, which are the words most likely
 * to be mangled by a phonetic round trip.
 *
 * The danda is converted first and excluded from the letter runs. It lives inside the same
 * Unicode block, so folding it into a word would leave that word not ending in a vowel --
 * and the word-ending rules would silently stop applying to it.
 */
export function transliterate(text: string): string {
  if (!text || !hasDevanagari(text)) return text;

  return text
    .replace(/[।॥]/g, ".")
    .replace(/[\u0900-\u0963\u0966-\u097f]+/g, (run) => transliterateWord(run));
}

/**
 * The Latin line to show *beneath* a transcript line, or null when there is nothing to add.
 *
 * Returns null for text that is already Latin so the caller renders one line, not two --
 * which means this quietly does nothing if the STT is ever switched to a Latin output mode.
 */
export function transliterationFor(text: string): string | null {
  if (!text || !hasDevanagari(text)) return null;
  const latin = transliterate(text);
  return latin === text ? null : latin;
}
